/**
 * LIS data layer — the single place that names the real `lis` endpoints (server/src/modules/lis/
 * routes.js, mounted under /api/v1) and their capability keys. Every screen reads/writes through
 * these hooks so the wire contract lives in one file. Server data lives ONLY in TanStack Query
 * (lab-scoped keys); mutations invalidate the keys they touch (L-1).
 */
import { api, qk, useApiMutation, useApiQuery } from "@/api";
import { useAuthStore } from "@/auth";
import type { QueryKey } from "@tanstack/react-query";
import type {
  Automate,
  CatalogueResponse,
  ChargementEntree,
  ChargementPositionnel,
  CreateAgentResult,
  Id,
  LisAgent,
  MappingAutomate,
  OnboardResult,
  ResultatEnAttente,
  TestConnectionResult,
} from "./types";

/**
 * Capability keys — the server's EXACT cap strings (server/src/modules/lis/routes.js). Mutating
 * actions gate on these via useCan; the server re-checks every request (these only hide/short UI).
 */
export const CAP = {
  /** Read the catalogue + list automates + run a connection test. */
  connect: "lis.automate.connect",
  /** Onboard / reconfigure an analyzer + bind mappings (the adapter-config owner action). */
  configure: "lis.adaptateur.configure",
  /** Ingest raw analyzer messages (simulator / connection entry). */
  ingest: "lis.resultat.ingest",
  /** Read the message worklist (mappings + quarantine). */
  messageRead: "lis.message.read",
  /** Positional load (LIS-9). */
  chargementCreate: "lis.chargement.create",
  chargementRead: "lis.chargement.read",
  chargementUpdate: "lis.chargement.update",
  chargementConfirm: "lis.chargement.confirm",
  /** NOVA-070 — steward the cloud LIS relay fleet (mint a pairing code, list, revoke). */
  agentManage: "lis.agent.manage",
} as const;

/** The module-level gate: the minimum to reach any LIS screen. */
export const MODULE_CAPS = [CAP.connect] as const;

// ── Query-key helpers (lab-scoped) ──────────────────────────────────────────────
function useLabId(): string | null {
  return useAuthStore((s) => s.user?.laboratoireId ?? null);
}

const keys = {
  catalogue: (labId: string | null, q: CatalogueQuery): QueryKey =>
    qk.scoped(labId, "lis", "catalogue", q),
  automates: (labId: string | null): QueryKey => qk.scoped(labId, "lis", "automates"),
  mappings: (labId: string | null, automateId: Id): QueryKey =>
    qk.scoped(labId, "lis", "automate", String(automateId), "mappings"),
  enAttente: (labId: string | null, motif?: string): QueryKey =>
    qk.scoped(labId, "lis", "en-attente", motif ?? "_"),
  chargement: (labId: string | null, id: Id): QueryKey =>
    qk.scoped(labId, "lis", "chargement", String(id)),
  agents: (labId: string | null): QueryKey => qk.scoped(labId, "lis", "agents"),
};

export interface CatalogueQuery {
  discipline?: string;
  fabricant?: string;
  q?: string;
}

// ── Discovery + onboarding ──────────────────────────────────────────────────────

/** GET /lis/catalogue — the onboarding discovery catalog (75 profiles). */
export function useCatalogue(query: CatalogueQuery = {}) {
  const labId = useLabId();
  const cleaned: Record<string, string> = {};
  if (query.discipline) cleaned["discipline"] = query.discipline;
  if (query.fabricant) cleaned["fabricant"] = query.fabricant;
  if (query.q) cleaned["q"] = query.q;
  return useApiQuery<CatalogueResponse>(keys.catalogue(labId, query), "/lis/catalogue", {
    query: cleaned,
    staleTime: 5 * 60_000, // the catalog is static config — cache hard
  });
}

/** GET /lis/automates — the analyzer fleet (with config). */
export function useAutomates() {
  const labId = useLabId();
  return useApiQuery<Automate[]>(keys.automates(labId), "/lis/automates");
}

export interface OnboardVars {
  profile_key?: string;
  code: string;
  libelle?: string;
  adresse_ip?: string;
  port?: number;
  overrides?: Record<string, unknown>;
}

/** POST /lis/automates — one-click onboarding from a catalog profile. */
export function useOnboardAnalyzer() {
  const labId = useLabId();
  return useApiMutation<OnboardResult, OnboardVars>(
    (vars) => api.post<OnboardResult>("/lis/automates", vars),
    { invalidate: [keys.automates(labId)] },
  );
}

/** POST /lis/automates/:id/test — validate config against adapter capabilities. */
export function useTestConnection() {
  return useApiMutation<TestConnectionResult, Id>((automateId) =>
    api.post<TestConnectionResult>(`/lis/automates/${automateId}/test`),
  );
}

export interface UpdateConfigVars {
  automateId: Id;
  patch: {
    adresse_ip?: string;
    port?: number;
    sens?: "unidirectionnel" | "bidirectionnel";
    mode_interaction?: "host_query" | "push" | "download";
    actif?: boolean;
  };
}

/** PATCH /lis/automates/:id/config — reconfigure an analyzer link. */
export function useUpdateConfig() {
  const labId = useLabId();
  return useApiMutation<unknown, UpdateConfigVars>(
    ({ automateId, patch }) => api.patch(`/lis/automates/${automateId}/config`, patch),
    { invalidate: [keys.automates(labId)] },
  );
}

// ── Mapping (LIS-5 admin loop) + quarantine worklist ────────────────────────────

/** GET /lis/automates/:id/mappings — the analyzer-code → ParametreLabo map. */
export function useMappings(automateId: Id | null) {
  const labId = useLabId();
  return useApiQuery<MappingAutomate[]>(
    keys.mappings(labId, automateId ?? "_"),
    `/lis/automates/${automateId}/mappings`,
    { enabled: automateId != null },
  );
}

export interface ConfirmMappingVars {
  mappingId: Id;
  parametre_labo_id: Id;
  methode?: string;
  unite_brute?: string;
  unite_canonique?: string;
  facteur_conversion?: number;
  decimales?: number;
}

/** POST /lis/mappings/:id/confirmer — bind a `propose` mapping → `actif` + auto-reconcile quarantine. */
export function useConfirmMapping(automateId: Id | null) {
  const labId = useLabId();
  return useApiMutation<unknown, ConfirmMappingVars>(
    ({ mappingId, ...body }) => api.post(`/lis/mappings/${mappingId}/confirmer`, body),
    {
      invalidate: [
        keys.mappings(labId, automateId ?? "_"),
        keys.enAttente(labId),
        keys.enAttente(labId, "code_inconnu"),
      ],
    },
  );
}

/** GET /lis/en-attente — the never-drop quarantine worklist (the AutomateAgent console). */
export function useEnAttente(motif?: string) {
  const labId = useLabId();
  const query: Record<string, string> = {};
  if (motif) query["motif"] = motif;
  return useApiQuery<ResultatEnAttente[]>(keys.enAttente(labId, motif), "/lis/en-attente", {
    query,
  });
}

// ── Positional load (LIS-9) ─────────────────────────────────────────────────────

export interface CreateChargementVars {
  automate_id: Id;
  reference: string;
}

/** POST /lis/chargements — open a positional load. */
export function useCreateChargement() {
  return useApiMutation<ChargementPositionnel, CreateChargementVars>((vars) =>
    api.post<ChargementPositionnel>("/lis/chargements", vars),
  );
}

/** GET /lis/chargements/:id — a load with its position → tube entries. */
export function useChargement(id: Id | null) {
  const labId = useLabId();
  return useApiQuery<ChargementPositionnel>(
    keys.chargement(labId, id ?? "_"),
    `/lis/chargements/${id}`,
    { enabled: id != null },
  );
}

export interface AddEntreeVars {
  chargementId: Id;
  position: string | number;
  tube_id: Id;
}

/**
 * POST /lis/chargements/:id/entrees — add a position → tube row to an open load. Invalidation is
 * targeted by the caller (it knows the chargement id) via the useChargementKey() invalidator on
 * success, so this mutation declares no static invalidate keys.
 */
export function useAddEntree() {
  return useApiMutation<ChargementEntree, AddEntreeVars>(({ chargementId, position, tube_id }) =>
    api.post<ChargementEntree>(`/lis/chargements/${chargementId}/entrees`, { position, tube_id }),
  );
}

/** Targeted invalidator for a single chargement (used after add/confirm/close). */
export function useChargementKey() {
  const labId = useLabId();
  return (id: Id): QueryKey => keys.chargement(labId, id);
}

/** POST /lis/chargements/:id/confirmer — seal the load (only then may a position bind a result). */
export function useConfirmChargement() {
  return useApiMutation<ChargementPositionnel, Id>((id) =>
    api.post<ChargementPositionnel>(`/lis/chargements/${id}/confirmer`),
  );
}

/** POST /lis/chargements/:id/clore — close a load. */
export function useCloseChargement() {
  return useApiMutation<ChargementPositionnel, Id>((id) =>
    api.post<ChargementPositionnel>(`/lis/chargements/${id}/clore`),
  );
}

// ── Cloud LIS relay agent cockpit (NOVA-070, lis.agent.manage) ───────────────────

/** GET /lis/agents — the relay fleet with the live online/offline badge folded on. */
export function useAgents() {
  const labId = useLabId();
  return useApiQuery<LisAgent[]>(keys.agents(labId), "/lis/agents", {
    // the badge is heartbeat-driven; poll so a connect/disconnect shows without a manual refresh.
    refetchInterval: 30_000,
  });
}

/**
 * POST /lis/agents — register a relay agent + mint a ONE-TIME pairing code. The response carries the raw
 * `pairing_code` (shown ONCE — the cockpit displays it then forgets it; the server stores only its hash).
 */
export function useCreateAgent() {
  const labId = useLabId();
  return useApiMutation<CreateAgentResult, { nom: string }>(
    (vars) => api.post<CreateAgentResult>("/lis/agents", vars),
    { invalidate: [keys.agents(labId)] },
  );
}

/** DELETE /lis/agents/:id — revoke a relay agent (the kill-switch; clears its token server-side). */
export function useRevokeAgent() {
  const labId = useLabId();
  return useApiMutation<{ ok: boolean; id: Id; statut: string }, Id>(
    (agentId) => api.delete(`/lis/agents/${agentId}`),
    { invalidate: [keys.agents(labId)] },
  );
}
